import React, { memo } from 'react'
import tw from 'twin.macro'
import { Tooltip, Typography } from '@mui/material'
import { QuestionMarkMinor } from '@shopify/polaris-icons'
import { colors } from 'src/theme'
import { ContractorStepStatus } from './ContractorStepStatus'
import { collectedDataStepIconsMap, CollectedDataStatus } from 'src/constants/collectedData'
import { getCollectDataStepTitle, isStepRecollecting } from 'src/utils/collectedData'
import { CollectedData } from 'src/interfaces/collectedData'
import { DateTime } from 'luxon'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

interface ContractorStepHeaderProps {
  collectedData: CollectedData
  delayed?: boolean
  stepStatus: CollectedDataStatus
  isRequestApprove?: boolean
  onRefreshContractors(): void | Promise<void>
}

const ContractorStepHeaderComponent = ({
  collectedData,
  delayed,
  stepStatus,
  isRequestApprove,
}: ContractorStepHeaderProps) => {
  const expiresAt = collectedData.expiresAt || undefined
  const expiresSoon = expiresAt
    ? dayjs(expiresAt).diff(dayjs(), 'day') <= 30 && dayjs(expiresAt).isAfter(dayjs())
    : false

  return (
    <div>
      {delayed && (
        <>
          <div css={tw`pb-4 flex flex-row items-center`}>
            <Typography css={tw`mr-1`} variant="overline">
              DELAYED STEP
            </Typography>
            <Tooltip
              color="black"
              title={
                <>
                  <Typography css={tw`mb-2`} variant="h6" color={colors.SHADES_GREY_200}>
                    Delayed step
                  </Typography>
                  <Typography variant="body2" color={colors.SHADES_GREY_300}>
                    A step the contractor cannot complete without a prior step being approved first.
                    To initiate this step for a contractor, please approve all steps in 'Waiting for
                    Approval' tab. They will then be sent a notification with a link to continue
                    onboarding.
                  </Typography>
                </>
              }
              placement="right-end"
            >
              <span css={tw`h-5 cursor-pointer`}>
                <QuestionMarkMinor width={20} />
              </span>
            </Tooltip>
          </div>
          <div css={tw`mb-4 border-0 border-b border-solid border-[#EDEDED]`} />
        </>
      )}

      <div css={tw`flex flex-row items-start justify-between gap-4`}>
        <div css={tw`flex items-center flex-1 min-w-0`}>
          <div css={tw`flex-shrink-0`}>
            {collectedDataStepIconsMap[collectedData.collectDataStep.type]}
          </div>
          <div css={tw`flex flex-col ml-3 min-w-0`}>
            <Typography css={tw`font-semibold text-black`} variant="h6">
              {getCollectDataStepTitle(collectedData)}
            </Typography>
            <div css={tw`mt-1`}>
              <ContractorStepStatus
                recollecting={isStepRecollecting(collectedData)}
                status={stepStatus}
                isRequestApprove={isRequestApprove}
                expiresSoon={expiresSoon}
              />
            </div>
          </div>
        </div>
        <div css={tw`flex flex-col items-end flex-shrink-0`}>
          <div css={tw`flex flex-col gap-1`}>
            {(expiresAt || stepStatus === CollectedDataStatus.WaitingForApproval) && (
              <div css={tw`flex items-baseline gap-2`}>
                <Typography variant="body2" color={colors.SHADES_GREY_400}>
                  Expiration
                </Typography>
                <Typography variant="body2" css={tw`text-black`}>
                  {expiresAt
                    ? DateTime.fromISO(expiresAt, { zone: 'utc' }).toFormat('MM/dd/yyyy')
                    : 'Not Set'}
                </Typography>
              </div>
            )}
            {(collectedData.effectiveAt ||
              stepStatus === CollectedDataStatus.WaitingForApproval) && (
              <div css={tw`flex items-baseline gap-2`}>
                <Typography variant="body2" color={colors.SHADES_GREY_400}>
                  Effective
                </Typography>
                <Typography variant="body2" css={tw`text-black`}>
                  {collectedData.effectiveAt
                    ? DateTime.fromISO(collectedData.effectiveAt, { zone: 'utc' }).toFormat(
                        'MM/dd/yyyy',
                      )
                    : 'Not Set'}
                </Typography>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export const ContractorStepHeader = memo(ContractorStepHeaderComponent)
